import _ from 'lodash';

import { FAVORITE_PLUS } from 'components/Favorite';
import {
  Events,
  getSegmentModelMetadata,
  handleAnalytics,
  PageNames,
  SEGMENT_HERO_COLLECTION_TITLE,
  SegmentSources,
  tealiumEventPageTypes,
} from '@foxcorp/delta-web-analytics';
import { isMobileBreakpoint } from 'helpers/BreakpointHelper';
import {
  isTypeVideo,
  isSpecial,
  isMovieSeries,
  getShowType,
  getRequiresAuth,
} from 'helpers/ContentHelper';
import { helperGetVideoUrl, helperGetRelativeUrl, helperGetLiveUrl } from 'helpers/RouteHelper';
import { formatDurationInSeconds, formatSubRatings, formatMetadata } from 'helpers/VideoHelper';
import { RESTART_TYPE_HOME } from 'screens/Watch/WatchHelpers';
import favoriteButtonTheme from 'themes/styleguide/redesign/ContentDetail/Hero/Favorite.css';
import { isVideoLive } from 'components/VideoPlayerGlobal/Helper';

const FRESHMAN = 'FRESHMAN';
const LATEST = 'LATEST';
const LIVE = 'LIVE';
const LIVE_LOCKED = 'LIVE LOCKED';
const LIVE_RESTART = 'LIVE RESTART';
const LOCKED = 'LOCKED';
const NEW = 'NEW';
const RESTART = 'RESTART';
const RESUME = 'RESUME';
const RETIRED = 'RETIRED';

export const formatMovieMetadata = ({ durationInSeconds, rating, subRatings, genres }) => {
  const meta = [
    durationInSeconds ? `${formatDurationInSeconds(durationInSeconds)}` : null,
    genres || null,
    rating ? `${rating} ${formatSubRatings(subRatings)}` : null,
  ];
  return formatMetadata(meta);
};

const isLatest = showcaseOrder => showcaseOrder === 'latest' || showcaseOrder === 'personalized';

const getSeriesPlayProps = ({ status, episode = {} }) => {
  const { seasonNumber, episodeNumber, displaySeasonAndEpisodeCounts } = episode;
  const {
    global_restartButton: restartText,
    global_resumeButton: resumeText,
    global_startWatchingButton: startWatchingText,
    global_watchLiveButton: watchLiveText,
    global_watchButton: watchText,
    seriesDetail_WatchLatest: latestText,
  } = window.Language;
  const playUrl =
    status === LIVE_RESTART ? getPlayButtonUrl(episode, true) : getPlayButtonUrl(episode);
  if (!playUrl || status === RETIRED) return { shouldRender: false };
  let url = playUrl;
  const seasonAndEpisodeNumberText =
    displaySeasonAndEpisodeCounts && seasonNumber && episodeNumber
      ? `S${seasonNumber} E${episodeNumber}`
      : '';
  const shouldRender = true;
  let icon;
  let text;
  switch (status) {
    case FRESHMAN:
      icon = 'play-rectangle';
      text = `${startWatchingText} ${seasonAndEpisodeNumberText}`;
      break;
    case LATEST:
      icon = 'play-rectangle';
      text = `${latestText} ${seasonAndEpisodeNumberText}`;
      break;
    case LIVE:
      icon = 'play-rectangle';
      text = watchLiveText;
      break;
    case LOCKED:
      icon = 'lock';
      text = startWatchingText;
      break;
    case NEW:
      icon = 'play-rectangle';
      text = `${watchText} ${seasonAndEpisodeNumberText}`;
      break;
    case RESTART:
      icon = 'restart-double';
      text = restartText;
      url = `${playUrl}mode/restart`;
      break;
    case RESUME:
      icon = 'play-rectangle';
      text = resumeText;
      break;
    case LIVE_LOCKED:
      icon = 'lock';
      text = watchLiveText;
      break;
    case LIVE_RESTART:
      icon = 'restart-double';
      text = restartText;
      url = `${playUrl}#${RESTART_TYPE_HOME}`;
      break;
    default:
      break;
  }
  return {
    icon,
    shouldRender,
    text,
    url,
  };
};

const getCurrentEpisode = ({ item, episodes, continueWatchingItems, isAuthenticated }) => {
  const {
    currentSeason,
    retired,
    liveListing,
    seriesType,
    showcaseOrder,
    latestEpisode,
    oldestEpisode,
  } = item || {};

  const { livePlayerScreenUrl } = liveListing || {};
  let status;
  let episode = {};
  if (retired) return { episode: {}, status: RETIRED };
  const { seasonNumber } = currentSeason || {};
  const isFreshman = seasonNumber === 1;
  if (continueWatchingItems.length > 0) {
    [episode] = continueWatchingItems;
    const { percentWatched, watched, episodeNumber } = episode;
    if (watched) {
      if (episodeNumber === episodes.length - 1) {
        status = RESTART;
      } else {
        episode = episodes[episodeNumber];
        status = LATEST;
      }
    } else {
      status = percentWatched > 10 && percentWatched < 90 ? RESUME : LATEST;
    }
  } else if (isFreshman) {
    [episode] = episodes;
    status = FRESHMAN;
  } else {
    [episode] = episodes;
    status = NEW;
  }
  if (!episode) {
    episode = {};
  }
  if (getRequiresAuth({ video: episode, isLive: !!livePlayerScreenUrl })) {
    status = LOCKED;
  }
  if (isVideoLive(liveListing)) {
    if (status === LOCKED) {
      status = `${LIVE} ${LOCKED}`;
    } else {
      status = LIVE;
    }
    episode = liveListing;
    return {
      episode,
      status,
    };
  }
  if (episode && episode.percentWatched > 0) {
    status = RESUME;
  }
  if (isSpecial(seriesType) || isMovieSeries(seriesType)) {
    episode = isLatest(showcaseOrder) ? latestEpisode : oldestEpisode;
  }

  return {
    episode,
    status,
  };
};

const getPlayButtonUrl = (item = {}, isLive = false) => {
  const { type, playerScreenUrl, videoId, livePlayerScreenUrl, callSign } = item;
  let link = '';
  const isVideo = type ? isTypeVideo(type) : false;
  if (isVideo || isLive) {
    link = playerScreenUrl
      ? helperGetRelativeUrl({ url: playerScreenUrl })
      : helperGetVideoUrl(videoId);
  } else if (livePlayerScreenUrl) {
    link = helperGetLiveUrl({ videoId, callSign });
  } else {
    link = helperGetRelativeUrl({ url: playerScreenUrl });
  }
  return link;
};

export const getHeroCommonProps = props => {
  const {
    breakpoint,
    item,
    disableScroll,
    episodes,
    continueWatchingItems = [],
    isAuthenticated,
  } = props;
  const { episode, status } = getCurrentEpisode({
    item,
    episodes,
    continueWatchingItems,
    isAuthenticated,
  });
  return {
    breakpoint,
    disableScroll,
    episode,
    item,
    status,
  };
};

export const getHeroContextPlayerProps = props => {
  const { item, breakpoint, playerVisibility, setPlayerVisibility, disableScroll } = props;
  const video = _.get(item, 'previewVideo.member.0', null);
  if (video) video.isTrailer = true;
  const title = _.get(video, 'name', '');
  const isMobile = isMobileBreakpoint(breakpoint);
  const visibility = playerVisibility;
  const promoDisabled = true;

  const onClose = () => {
    setPlayerVisibility(false);
    disableScroll({ disableScroll: false, bodyOnly: false });
  };

  return {
    ...getHeroCommonProps(props),
    video,
    title,
    isMobile,
    visibility,
    promoDisabled,
    onClose,
  };
};

export const getHeroFavoriteButtonProps = props => {
  const { item, favoritesManager: manager, favoritableItems, recommendationId } = props;
  const { id: showID, seriesType, network: networkId, name: seriesName, description } = item;
  const actionLocation = _.get(tealiumEventPageTypes, PageNames.DETAIL);
  const location = _.get(tealiumEventPageTypes, PageNames.FAVORITES_DETAIL);
  const showType = getShowType({ seriesType });
  const defaultText = 'Watchlist';
  const favoritedText = 'Added to Watchlist';
  const iconType = FAVORITE_PLUS;

  const data = {
    actionLocation,
    favoritableItems,
    favoriteType: showType,
    location,
    networkId,
    recommendationId,
    seriesName,
    showID,
    source: SegmentSources.DETAIL,
  };
  const favorites = _.get(manager, 'favorites');

  const clickHandler = () => {
    const props = {
      page_action_type: 'add to favorites',
      page_collection_count: 1,
      page_collection_title: SEGMENT_HERO_COLLECTION_TITLE,
      page_collection_type: 'general',
      page_item_type: 'hero',
      page_item_state: 'static image',
      page_item_container_title: seriesName,
      page_item_vertical_position: 1,
      page_item_horizontal_position: 1,
      page_item_title: defaultText,
      page_item_subtitle: description,
      page_item_cc: 'cc off',
      page_item_volume: 'volume off',
      page_playlist_length: 1,
      page_video_uid: item.latestEpisode?.uID,
      page_webview_url: window.location.href,
    };
    handleAnalytics({
      event: Events.HERO_ITEM_SELECT_ACTION,
      payoad: props,
    });
  };

  const favoriteButtonProps = {
    ...getHeroCommonProps(props),
    clickHandler,
    data,
    defaultText,
    favoritedText,
    favorites,
    iconType,
    manager,
    theme: favoriteButtonTheme,
  };
  return favoriteButtonProps;
};

export const getHeroPlayButtonProps = props => {
  const { episode, item, status } = getHeroCommonProps(props);
  const { fullEpisodeCount, name, description } = item;
  return {
    ...getHeroCommonProps(props),
    ...getSeriesPlayProps({ status, episode, item }),
    description,
    fullEpisodeCount,
    name,
  };
};

export const getHeroRestartButtonProps = props => {
  const { episode = {}, item = {} } = getHeroCommonProps(props);
  const { percentWatched, watched, livePlayerScreenUrl } = episode;
  const { fullEpisodeCount, name, description, liveListing = {} } = item;
  const { playerScreenUrl = null } = liveListing;
  const shouldRender = !!playerScreenUrl || !!(percentWatched && !watched && percentWatched > 10);
  const { text, icon, url } = getSeriesPlayProps({
    status: livePlayerScreenUrl ? LIVE_RESTART : RESTART,
    episode,
  });

  return {
    ...getHeroCommonProps(props),
    description,
    fullEpisodeCount,
    icon,
    isRestart: true,
    name,
    shouldRender,
    text,
    url,
  };
};

export const getHeroTrailerButtonProps = props => {
  const { item, setPlayerVisibility, disableScroll } = props;
  const { description, fullEpisodeCount, name } = item;
  const previewVideo = _.get(item, 'previewVideo.member.0', null);
  const shouldRender = !!previewVideo;
  const text = 'Trailer';
  const icon = 'filmstrip';
  const viewBox = '0 0 18 18';
  const clickHandler = () => {
    setPlayerVisibility(true);
    disableScroll({ disableScroll: true, bodyOnly: true });

    const props = {
      page_action_type: 'trailer',
      page_collection_count: 1,
      page_collection_title: SEGMENT_HERO_COLLECTION_TITLE,
      page_collection_type: 'general',
      page_item_type: 'hero',
      page_item_state: 'static image',
      page_item_container_title: name,
      page_item_vertical_position: 1,
      page_item_horizontal_position: 1,
      page_item_title: text,
      page_item_subtitle: description,
      page_item_cc: 'cc off',
      page_item_volume: 'volume off',
      page_playlist_length: 1,
      page_video_uid: previewVideo.uID,
      page_webview_url: window.location.href,
      ...getSegmentModelMetadata(),
    };
    handleAnalytics({
      event: Events.HERO_ITEM_SELECT_ACTION,
      payload: props,
    });
  };

  return {
    ...getHeroCommonProps(props),
    clickHandler,
    description,
    fullEpisodeCount,
    icon,
    name,
    shouldRender,
    text,
    viewBox,
  };
};
